import { type HTMLAttributes, useState } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'

const avatarVariants = cva(
  'relative inline-flex items-center justify-center rounded-full font-medium overflow-hidden shrink-0',
  {
    variants: {
      size: {
        /* `xs` carried a raw 9-pixel arbitrary value until 07.08.2026 — below
           the 12.5px floor, below even `text-micro` (10px), and not a ramp
           token at all, so neither check-type-floor nor D-7's sweep could see
           it. layout-gate found it rendered: `TINYTEXT 9px "HF"` and `"IB"` on
           manage-rom at both mobile and narrow.
           (That class literal is deliberately not spelled out here. Tailwind
           scans comments too, so quoting it kept generating the very rule this
           removed — the same trap `check-grid` hit when it counted a class name
           cited in a comment.)
           It takes `sm`'s answer, for `sm`'s reason — the note below is as true
           of a 24px circle as of a 32px one: an initial is read, not decorated.
           What does NOT carry over is the glyph COUNT. Two initials at 12.5px
           overflow 24px («WW» 29.9, «MM» 27.1, «MW» 28.3, all measured in this
           face), so `xs` renders one — see `getInitials`. */
        xs: 'size-6 text-2xs',
        /* 46 · text-2xs (12.5px), NOT text-micro. PASTE-BOLK-4 lists avatar in
           ALLOW_MICRO, and following that literally moved these initials from
           11px to 10px — DOWN, in the one stage whose purpose is raising the
           floor. layout-gate caught it as 8 new findings. An initial is read,
           not decorated, and 12.5px is 39% of a 32px circle, which is normal.
           kbd and badge counters stay on text-micro; they are true glyphs. */
        sm: 'size-8 text-2xs',
        default: 'size-10 text-label',
        lg: 'size-14 text-lg',
        xl: 'size-20 text-2xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

/* Sweep 24.10.2026 (semantic colour): a face is not a state, so the swatches are the identity
   family (`--tint-cat-*`, the same six hues the calendar uses for categories), never the status
   ramps. They were primary, accent, WARNING, ERROR, info and SUCCESS: every screen showed the
   signed-in organiser in red. Category hue belongs to the background. Initials use the paired avatar ink;
   the category colour itself fails AA on its own tint in light mode. */
// Literal strings: Tailwind only generates classes it can read whole.
const AVATAR_COLORS = [
  'bg-[color-mix(in_oklab,var(--tint-cat-1)_22%,var(--surface-default))] text-[var(--avatar-fg)]',
  'bg-[color-mix(in_oklab,var(--tint-cat-2)_22%,var(--surface-default))] text-[var(--avatar-fg)]',
  'bg-[color-mix(in_oklab,var(--tint-cat-3)_22%,var(--surface-default))] text-[var(--avatar-fg)]',
  'bg-[color-mix(in_oklab,var(--tint-cat-4)_22%,var(--surface-default))] text-[var(--avatar-fg)]',
  'bg-[color-mix(in_oklab,var(--tint-cat-5)_22%,var(--surface-default))] text-[var(--avatar-fg)]',
  'bg-[color-mix(in_oklab,var(--tint-cat-6)_22%,var(--surface-default))] text-[var(--avatar-fg)]',
] as const

function getColorForName(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

/**
 * `max` exists because the glyph count is a function of the space, not a
 * constant. Measured at 12.5px in this face: «WW» is 29.9px and «MM» 27.1px,
 * against the 24px circle `xs` draws. The container is `overflow-hidden`, so
 * those would not break the layout — they would render as a W with the second
 * letter sliced off, which is worse than a small initial, not better. One
 * letter at that size is ~15px and sits inside with room.
 */
function getInitials(name: string, max = 2): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, max)
    .join('')
    .toUpperCase()
}

interface AvatarProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string | null
  name: string
  ring?: boolean
  online?: boolean
  /** One of an overlapping stack: a surface-coloured edge separates it from its neighbour. */
  stacked?: boolean
}

function Avatar({ className, size, src, name, ring, online, stacked, ...props }: AvatarProps) {
  // `xs` is a 24px circle: two wide initials overflow it and get clipped, so it
  // takes one. Decided in the primitive rather than at the twenty call sites —
  // none of them knows the font metrics, and all of them would have to agree.
  const initials = getInitials(name, size === 'xs' ? 1 : 2)
  const colorClass = getColorForName(name)

  // Track which src we've seen errored. Resetting via key on the inner <img>
  // so the browser re-attempts when src changes — no useEffect needed.
  const [erroredSrc, setErroredSrc] = useState<string | null>(null)
  const showImage = !!src && erroredSrc !== src

  return (
    <div
      className={cn(
        avatarVariants({ size }),
        ring && 'ring-2 ring-[var(--surface-default)] ring-offset-2 ring-offset-[var(--color-primary-400)]',
        stacked && 'ring-2 ring-[var(--surface-default)]',
        placed(className, 'Avatar', props)
      )}
      title={name}
      {...props}
    >
      {showImage ? (
        <img
          key={src}
          src={src ?? undefined}
          /* alt="" — the name is almost always rendered as text beside the
             avatar, so repeating it makes screen readers announce it twice
             (axe `image-redundant-alt`). The image is decorative here. */
          alt=""

          className="size-full object-cover"
          onError={() => setErroredSrc(src ?? null)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className={cn('flex size-full items-center justify-center', colorClass)}>
          {initials}
        </div>
      )}
      {online && (
        <span className="absolute bottom-0 right-0 size-2.5 rounded-full bg-[var(--color-success-500)] ring-2 ring-[var(--surface-default)]" />
      )}
    </div>
  )
}

interface AvatarGroupProps {
  children: React.ReactNode
  max?: number
  className?: string
}

function AvatarGroup({ children, max, className }: AvatarGroupProps) {
  const childArray = Array.isArray(children) ? children : [children]
  const visible = max ? childArray.slice(0, max) : childArray
  const overflow = max ? childArray.length - max : 0

  return (
    <div className={cn('flex items-center -space-x-2', placed(className, 'AvatarGroup'))}>
      {visible.map((child, i) => (
        <div key={i} className="relative" style={{ zIndex: visible.length - i }}>
          {child}
        </div>
      ))}
      {overflow > 0 && (
        <div className="relative flex size-8 items-center justify-center rounded-full bg-[var(--surface-muted)] text-2xs font-medium text-[var(--text-secondary)] ring-2 ring-[var(--surface-default)]">
          +{overflow}
        </div>
      )}
    </div>
  )
}

export { Avatar, AvatarGroup }
export type { AvatarProps }
